<template>
    <b-modal
        @ok="handleCreateOk"
        ref="create-modal"
        :title="$t('waiting_room.create_modal.title')"
        :cancel-title="$t('waiting_room.btn.cancel')"
        ok-title="Create"
        size="lg"
        scrollable>

      <h4 class="text-primary">{{ $t('waiting_room.create_modal.section_basic_params_title') }}</h4>

      <custom-form-input
        :label="$t('waiting_room.create_modal.label.name')"
        :input-type="'default'"
        :isValid="validateState('name')"
        translationName='waiting_room.create_modal.name'
        :isTooltipAvailable="false"
        v-model="data.name">
      </custom-form-input>

      <custom-form-input
          :label="$t('waiting_room.create_modal.label.question_category')"
          :input-type="'select'"
          :isValid="validateState('questionCategoryId')"
          translationName='waiting_room.create_modal.question_category_id'
          :isTooltipAvailable="true"
          v-model="data.questionCategoryId"
          :select-options="{options: questionCategories, textField: 'label', valueField: 'id'}">
      </custom-form-input>

      <custom-form-input
          :label="$t('waiting_room.create_modal.label.wallet_currency')"
          :input-type="'select'"
          :isValid="validateState('walletCurrency')"
          translationName='waiting_room.create_modal.wallet_currency'
          :isTooltipAvailable="true"
          v-model="data.walletCurrency"
          :select-options="{options: walletCurrencies, textField: 'textField', valueField: 'valueField'}">
      </custom-form-input>

      <custom-form-input
          :label="$t('waiting_room.create_modal.label.max_players')"
          :input-type="'select'"
          :isValid="validateState('maxPlayers')"
          translationName='waiting_room.create_modal.max_players'
          :isTooltipAvailable="true"
          v-model="data.maxPlayers"
          @input="setQuizRankingPointsStructure($event)"
          :select-options="{options: maxPlayersQuantities}">
      </custom-form-input>

      <custom-form-input
          :label="$t('waiting_room.create_modal.label.buy_in')"
          :input-type="'default'"
          :isValid="validateState('buyIn')"
          translationName='waiting_room.create_modal.buy_in'
          :isTooltipAvailable="true"
          v-model="data.buyIn">
      </custom-form-input>

      <custom-form-input
          :label="$t('waiting_room.create_modal.label.prize_pool')"
          :input-type="'default'"
          :isValid="validateState('prizePool')"
          translationName='waiting_room.create_modal.prize_pool'
          :isTooltipAvailable="true"
          v-model="data.prizePool">
      </custom-form-input>

      <div class="p-4 mt-4 mb-4 bg-light" v-if="data.quizRankingPoints">
        <h4 class="text-primary">{{ $t('waiting_room.create_modal.label.quiz_ranking_points') }}</h4>
        <div class="row" v-for="(point, index) in Number(data.maxPlayers)" :key="index">
          <div class="col-sm-12">
            <b-form-group>
              <div class="row d-flex align-items-center">
                <div class="col-12 col-lg-6">
                  {{`${$t('waiting_room.create_modal.label.quiz_ranking_points_for_position')}: ${index + 1}`}}
                </div>
                <div class="col-12 col-lg-6">
                  <b-form-input
                      type="number"
                      min="0"
                      v-model="data.quizRankingPoints[index].points"
                      @input="$v.data.quizRankingPoints.$each[index].points.$model = $event.trim()"
                      :state="$v.data.quizRankingPoints && !$v.data.quizRankingPoints.$each[index].points.$dirty ? null : !$v.data.quizRankingPoints.$each[index].points.$error"
                      placeholder="Quiz ranking points"
                      trim>
                  </b-form-input>
                </div>
              </div>
            </b-form-group>
          </div>
        </div>
      </div>

      <div class="p-4 mt-4 mb-4 bg-light">
        <h4 class="text-primary">{{ $t('waiting_room.create_modal.section_stages_params_title') }}</h4>

        <div class="row">
          <div class="col-sm-8">
            <b-form-select id="form-chooseRoundType"
                           v-model="addStageRoundType"
                           :options="roundTypes">
              <b-form-select-option value="null" disabled>{{ $t('select.placeholder') }}</b-form-select-option>
            </b-form-select>
          </div>
          <div class="col-sm-4">
            <button class="btn btn-success" @click="addStage">{{ $t('waiting_room.btn.add_stage') }}</button>
          </div>
        </div>
      </div>

      <div
          v-for="(stage,index) in data.stagesParameters" :key="index"
          class="p-4"
          :style="{ background: index % 2 === 0 ? '#dee2e6' : '' }"
          :class="[index % 2 === 0 ? 'mb-0': 'mb-4' ]"
      >
        <StageConfiguration
            :ref="'ref-stage-'+index"
            :round-type="stage.roundType"
            :cms-pages-list="cmsPagesList"
            :clonedData="stage">
        </StageConfiguration>
        <button class="btn btn-danger" @click="removeStage(index)">{{ $t('waiting_room.btn.remove_stage') }}</button>
      </div>

      <h4 class="text-primary mt-4">{{ $t('waiting_room.create_modal.section_final_stage_params_title') }}</h4>

      <custom-form-input
          :label="$t('waiting_room.create_modal.label.points_multiplier')"
          :input-type="'default'"
          :isValid="validateState(['finalStageParameters','pointsMultiplier'])"
          translationName='waiting_room.create_modal.points_multiplier'
          :isTooltipAvailable="true"
          v-model="data.finalStageParameters.pointsMultiplier">
      </custom-form-input>

      <custom-form-input
          :label="$t('waiting_room.create_modal.label.minimum_amount_of_time')"
          :input-type="'default'"
          :isValid="validateState(['finalStageParameters','minAmountOfTime'])"
          translationName='waiting_room.create_modal.min_amount_of_time'
          :isTooltipAvailable="true"
          v-model="data.finalStageParameters.minAmountOfTime">
      </custom-form-input>

      <custom-form-input
          :label="$t('waiting_room.create_modal.label.number_of_rounds_to_change_time')"
          :input-type="'default'"
          :isValid="validateState(['finalStageParameters','numberOfRoundsToChangeTime'])"
          translationName='waiting_room.create_modal.number_of_rounds_to_change_time'
          :isTooltipAvailable="true"
          v-model="data.finalStageParameters.numberOfRoundsToChangeTime">
      </custom-form-input>

      <custom-form-input
          :label="$t('waiting_room.create_modal.label.cms_page_key_name')"
          :input-type="'select'"
          :isValid="validateState(['finalStageParameters','cmsPageKeyName'])"
          translationName='waiting_room.create_modal.cms_page_key_name'
          :isTooltipAvailable="true"
          v-model="data.finalStageParameters.cmsPageKeyName"
          :select-options="{options: cmsPagesList, textField: 'title', valueField: 'keyName'}">
      </custom-form-input>

      <custom-form-input
          :label="$t('waiting_room.create_modal.label.question_time_bonus')"
          :input-type="'default'"
          :isValid="validateState(['finalStageParameters','roundConfiguration','questionTimeBonus'])"
          translationName='waiting_room.create_modal.question_time_bonus'
          :isTooltipAvailable="true"
          v-model="data.finalStageParameters.roundConfiguration.questionTimeBonus">
      </custom-form-input>

      <custom-form-input
          :label="$t('waiting_room.create_modal.label.question_time_penalty')"
          :input-type="'default'"
          :isValid="validateState(['finalStageParameters','roundConfiguration','questionTimePenalty'])"
          translationName='waiting_room.create_modal.question_time_penalty'
          :isTooltipAvailable="true"
          v-model="data.finalStageParameters.roundConfiguration.questionTimePenalty">
      </custom-form-input>

      <custom-form-input
          :label="$t('waiting_room.create_modal.label.password')"
          :input-type="'default'"
          translationName='waiting_room.create_modal.password'
          :isValid="validateState('password')"
          :showUpperCaseValue="true"
          v-model="data.password">
      </custom-form-input>

    </b-modal>
</template>

<script>
import axios from 'axios';
import {helpers, integer, minValue, required} from "vuelidate/lib/validators";
import {validationMixin} from "vuelidate";
import StageConfiguration from "../StageConfiguration";
import moment from "moment";
import CustomFormInput from "../../../components/Form/CustomFormInput";

//had to define own regex for fractions - built in types (decimal, numeric) didn't satisfy business requirements
 const fraction = helpers.regex('fraction', /^[-]?\d+([.,]\d+)?$/);
//had to define own regex for not negative fractions - built in minValue type crapped out in conjunction with fractions
const notNeg = helpers.regex('notNeg', /^[^-]/);

//TODO: import / load / get locale the right way
let locale = locale ? locale : "en";

export default {
  components: {StageConfiguration, CustomFormInput},
  props: ['apiKey'],
  mixins: [validationMixin],
  validations: {
    data: {
      name: {required},
      questionCategoryId: {required},
      maxPlayers: {required, integer},
      buyIn: {required, fraction, notNeg},
      quizRankingPoints: {
        $each: {
          points: {
            required,
            minValue: minValue(0),
            integer
          }
        }
      },
      prizePool: {required, fraction, notNeg},
      walletCurrency: {required},
      finalStageParameters: {
        pointsMultiplier: {required, fraction, notNeg},
        minAmountOfTime: {required, integer, minValue: minValue(1)},
        numberOfRoundsToChangeTime: {required, integer, minValue: minValue(1)},
        cmsPageKeyName: { required },
        roundConfiguration: {
          questionTimeBonus: {required, integer, minValue: minValue(0)},
          questionTimePenalty: {required, integer, minValue: minValue(0)},
        },
      },
      password: {}
    }
  },
  data() {
    return {
      addStageRoundType: null,
      data: this.getInitialData(),
      walletCurrencies: [
        {textField: this.$t('FUN_COINS'), valueField: 'FUN_COINS'},
        {textField: this.$t('QUIZ_COINS'), valueField: 'QUIZ_COINS'}
      ],
      roundTypes: ['COLLECTING', 'SPRINTING', 'BETTING', 'CONQUERING'],
      maxPlayersQuantities: [2, 3, 4],
      cmsPagesList: [],
      questionCategories: []
    };
  },
  filters: {},
  mounted() {
    axios.get('/api/question/question-category?limit=1000')
        .then((resp) => {
          this.questionCategories = resp.data.items.map(c => {
            return {
              id: c.id,
              label: this.$t('category.'+c.name)
            }
          });
        })
        .catch((err) => {
          console.log(err)
        })
    axios.get(`/api/resource/children/round-rules/${locale}`)
        .then((resp) => {
          this.cmsPagesList = resp.data[0].__children;
        })
        .catch((err) => {
          console.log(err)
        });
    // this.$refs['create-modal'].show();
  },
  methods: {
    setQuizRankingPointsStructure(e) {
      const quizRankingPoints = [];

      for(let i = 0; i <= Number(e); i++) {
        quizRankingPoints[i] = {
          position: (i+1).toString(),
          points: 0
        };
      }

      this.data.quizRankingPoints = quizRankingPoints;
    },
    validateState(entry) {
      if (entry.constructor.name === 'Array') {
        switch (entry.length) {
          case 3:
            entry = this.$v.data[entry[0]][entry[1]][entry[2]];
            break;
          case 2:
            entry = this.$v.data[entry[0]][entry[1]];
            break;
        }
      } else {
        entry = this.$v.data[entry];
      }
      const {$dirty, $error} = entry;
      return $dirty ? !$error : null;
    },
    addStage() {
      if (this.addStageRoundType !== null) {
        this.data.stagesParameters.push({
          stageOrder: this.data.stagesParameters.length + 1,
          roundType: this.addStageRoundType,
          roundConfiguration: {}
        });
      }
    },
    removeStage(index) {
      this.data.stagesParameters.splice(index, 1);
    },
    handleCreateOk(bvModalEvt) {
      bvModalEvt.preventDefault()
      this.handleCreateSubmit()
    },
    handleCreateSubmit() {
      this.$v.$touch()
      let isValid = this.$v.$invalid === false;
      for (let i in this.data.stagesParameters) {
        let t = this.$refs['ref-stage-' + i][0].isValid();
        isValid = isValid && t;
      }

      if (isValid) {
        let data = {...this.data, password: this.data.password ? this.data.password.toUpperCase() : null };
        let stagesParameters = [];
        for (let i in this.data.stagesParameters) {
          let t = this.$refs['ref-stage-' + i][0].getData();
          t.stageOrder = i * 1;
          stagesParameters.push(t);
        }

        data.stagesParameters = stagesParameters;
        this.recodeNumericValues(data);

        const rankingPoints = this.data.quizRankingPoints.map(item => {
          const obj = {position: item.position, points: Number(item.points)};
          return obj;
        })

        data.quizRankingPoints = rankingPoints;

        data.createdAt = moment(Date.now()).format('YYYY-MM-DD HH:mm:ss');
        let _that = this;
        axios.post('/api/game-waiting-room', data)
            .then(function () {
              setTimeout(() => {
                _that.$parent.$emit('refreshGrid')
                _that.$refs['create-modal'].hide()
              }, 1000);
            }).catch(function () {
        });
      }
    },
    parseQuizRankinPointsValuesToObject() {
      Object.keys(this.data.quizRankingPoints).forEach((el) => {
        this.data.quizRankingPoints[el] = Number(this.data.quizRankingPoints[el])
      });
    },
    recodeNumericValues(data) {
      const parseStringToFloat = (value) => {
        if(!value) return value;
        return parseFloat(value.toString().replace(/,/g, '.'));
      }

      data.buyIn = parseStringToFloat(data.buyIn);
      data.prizePool = parseStringToFloat(data.prizePool);
      // data.quizRankingPoints = parseStringToFloat(data.quizRankingPoints);
      data.finalStageParameters.pointsMultiplier = parseStringToFloat(data.finalStageParameters.pointsMultiplier);
      data.finalStageParameters.minAmountOfTime = parseStringToFloat(data.finalStageParameters.minAmountOfTime);
      data.finalStageParameters.numberOfRoundsToChangeTime = parseStringToFloat(data.finalStageParameters.numberOfRoundsToChangeTime);
      data.finalStageParameters.roundConfiguration.questionTimeBonus = parseStringToFloat(data.finalStageParameters.roundConfiguration.questionTimeBonus);
      data.finalStageParameters.roundConfiguration.questionTimePenalty = parseStringToFloat(data.finalStageParameters.roundConfiguration.questionTimePenalty);
      for (let i in data.stagesParameters) {
        data.stagesParameters[i].numberOfRounds = parseStringToFloat(data.stagesParameters[i].numberOfRounds);
        data.stagesParameters[i].roundConfiguration.timeForAnswer = parseStringToFloat(data.stagesParameters[i].roundConfiguration.timeForAnswer);
        data.stagesParameters[i].roundConfiguration.numberOfPoints = parseStringToFloat(data.stagesParameters[i].roundConfiguration.numberOfPoints);
        data.stagesParameters[i].roundConfiguration.pointsMultiplier = parseStringToFloat(data.stagesParameters[i].roundConfiguration.pointsMultiplier);
        data.stagesParameters[i].roundConfiguration.availableStakes =
            data.stagesParameters[i].roundConfiguration.availableStakes?.map( stake => parseStringToFloat(stake));

        if (data.stagesParameters[i].roundConfiguration.pointsToSteal) {
          data.stagesParameters[i].roundConfiguration.pointsToSteal = parseStringToFloat(data.stagesParameters[i].roundConfiguration.pointsToSteal);
        }

      }
    },
    getInitialData() {
      return {
        name: null,
        lobbyId: 'INSTANT',
        questionCategoryId: null,
        maxPlayers: null,
        buyIn: null,
        quizRankingPoints: null,
        prizePool: null,
        walletCurrency: null,
        stagesParameters: [],
        finalStageParameters: {
          pointsMultiplier: null,
          minAmountOfTime: null,
          numberOfRoundsToChangeTime: null,
          roundConfiguration: {
            questionTimeBonus: null,
            questionTimePenalty: null,
          },
          cmsPageKeyName: null
        },
        password: null
      }
    },
    setData(data) {
        this.data = {...data, ...data.gameParameters};
        this.data.id = null;
        this.data.lobbyId = 'INSTANT';
        this.data.name = data.name;
        this.$v.$reset();
    },
    showModal(){
      this.$v.$reset();
      this.$data.data = this.getInitialData();
      this.$refs['create-modal'].show();
    }
  }
}
</script>
